import type {
  CitationFacet,
  CitationValence,
  StaffCitation,
  StaffConfidence,
  StaffRating,
  TicketHealth,
} from '../types/staff';

/** One segment of profile prose: plain text or an inline `[ev:123]` evidence reference. */
export type EvidenceTextSegment = { type: 'text'; value: string } | { type: 'ev'; id: number };

/**
 * Inline evidence citations as the steward bot writes them: a bracketed token or list
 * (`[ev:12]`, `[ev:12, ev:34]`) or a bare `ev:12`. Whitespace directly after a citation
 * is swallowed so the surrounding sentence closes up cleanly.
 */
const EV_CITATION = /\[\s*ev:\d+(?:\s*,\s*ev:\d+)*\s*\]\s*|\bev:\d+\b\s*/g;
const EV_ID = /ev:(\d+)/g;

/**
 * Splits steward prose containing evidence citations into text and evidence segments
 * (one `ev` segment per cited id, in order).
 */
export function parseEvidenceRefs(text: string): EvidenceTextSegment[] {
  const segments: EvidenceTextSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(EV_CITATION)) {
    const start = match.index ?? 0;
    if (start > last) segments.push({ type: 'text', value: text.slice(last, start) });
    for (const id of match[0].matchAll(EV_ID)) segments.push({ type: 'ev', id: Number(id[1]) });
    last = start + match[0].length;
  }
  if (last < text.length) segments.push({ type: 'text', value: text.slice(last) });
  return segments;
}

/** Evidence ids cited anywhere in the prose, in first-seen order without duplicates. */
export function extractEvidenceIds(text: string): number[] {
  const ids: number[] = [];
  for (const match of text.matchAll(EV_CITATION)) {
    for (const id of match[0].matchAll(EV_ID)) {
      const n = Number(id[1]);
      if (!ids.includes(n)) ids.push(n);
    }
  }
  return ids;
}

/**
 * Removes every evidence citation from prose. Used wherever the prose is displayed:
 * the evidence is shown as chips (below the note, or after the text) instead of inline.
 */
export function stripEvidenceRefs(text: string): string {
  return text
    .replace(EV_CITATION, '')
    .replace(/\s+([.,;:!?)])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+$/, '');
}

export interface ValenceCounts {
  good: number;
  neutral: number;
  poor: number;
  total: number;
}

export interface FacetSummary extends ValenceCounts {
  facet: string;
}

export interface TicketGroup {
  ticketId: string;
  health: TicketHealth | string;
  citations: StaffCitation[];
  latestAt: number;
}

export interface CitationSummary {
  total: number;
  byValence: ValenceCounts;
  /** Sorted by total descending. */
  byFacet: FacetSummary[];
  /** Sorted by citation count desc, then most recent observation desc. */
  byTicket: TicketGroup[];
}

const emptyCounts = (): ValenceCounts => ({ good: 0, neutral: 0, poor: 0, total: 0 });

function bump(counts: ValenceCounts, valence: string): void {
  if (valence === 'good' || valence === 'neutral' || valence === 'poor') counts[valence] += 1;
  counts.total += 1;
}

/** Aggregates a profile's citations by valence, facet and ticket. */
export function summarizeCitations(citations: Record<string, StaffCitation> | null | undefined): CitationSummary {
  const all = Object.values(citations ?? {});
  const byValence = emptyCounts();
  const facetMap = new Map<string, FacetSummary>();
  const ticketMap = new Map<string, TicketGroup>();

  for (const c of all) {
    bump(byValence, c.valence);

    let facet = facetMap.get(c.facet);
    if (!facet) {
      facet = { facet: c.facet, ...emptyCounts() };
      facetMap.set(c.facet, facet);
    }
    bump(facet, c.valence);

    const key = c.ticket_id || 'unknown';
    let group = ticketMap.get(key);
    if (!group) {
      group = { ticketId: key, health: '', citations: [], latestAt: 0 };
      ticketMap.set(key, group);
    }
    group.citations.push(c);
    if (c.observed_at > group.latestAt) group.latestAt = c.observed_at;
    if (!group.health && c.ticket_health) group.health = c.ticket_health;
  }

  for (const group of ticketMap.values()) {
    group.citations.sort((a, b) => a.observed_at - b.observed_at);
  }

  return {
    total: all.length,
    byValence,
    byFacet: [...facetMap.values()].sort((a, b) => b.total - a.total || a.facet.localeCompare(b.facet)),
    byTicket: [...ticketMap.values()].sort(
      (a, b) => b.citations.length - a.citations.length || b.latestAt - a.latestAt,
    ),
  };
}

/** `follow_through` → `follow-through`. */
export function prettyFacet(facet: string): string {
  return facet.replace(/_/g, '-');
}

/**
 * Compact duration: `3d`, `1d 4h`, `2h 10m`, `5m`. Values under a minute round up to `1m`.
 */
export function formatSeconds(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (d >= 3) return `${d}d`;
  if (d >= 1) return `${d}d ${h}h`;
  if (h >= 1) return `${h}h ${m}m`;
  return `${Math.max(1, m)}m`;
}

/** Relative "ago" string from a unix-seconds timestamp against a unix-seconds "now". */
export function agoFromSeconds(timestamp: number, nowSeconds: number): string {
  return `${formatSeconds(nowSeconds - timestamp)} ago`;
}

export const SIGNAL_RATINGS: StaffRating[] = ['strong', 'solid', 'mixed', 'weak', 'unknown'];

/** `strong` is the rare top rating (above `solid`); it gets a highlighted display. */
export const isStrongRating = (rating: StaffRating | string): boolean => rating === 'strong';

/** Badge classes for a signal rating (semantic palette, theme-safe). */
export function ratingTone(rating: StaffRating | string): string {
  switch (rating) {
    case 'strong':
      return 'bg-purple-500/15 text-purple-300 border-purple-500/30';
    case 'solid':
    case 'good':
      return 'bg-green-500/20 text-green-300 border-green-500/30';
    case 'mixed':
      return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
    case 'weak':
    case 'poor':
      return 'bg-red-500/20 text-red-300 border-red-500/30';
    default:
      return 'bg-glass-200/10 text-glass-400 border-glass-200/20';
  }
}

/** Badge classes for profile confidence. */
export function confidenceTone(confidence: StaffConfidence | string): string {
  switch (confidence) {
    case 'high':
      return 'bg-green-500/20 text-green-300 border-green-500/30';
    case 'medium':
      return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    case 'low':
      return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
    default:
      return 'bg-glass-200/10 text-glass-400 border-glass-200/20';
  }
}

/** Badge classes for a ticket's health at the time of observation. */
export function ticketHealthTone(health: TicketHealth | string): string {
  switch (health) {
    case 'healthy':
      return 'bg-green-500/20 text-green-300 border-green-500/30';
    case 'slow':
      return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
    case 'stalled':
      return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
    case 'abandoned':
      return 'bg-red-500/20 text-red-300 border-red-500/30';
    default:
      return 'bg-glass-200/10 text-glass-400 border-glass-200/20';
  }
}

/** Solid swatch classes per valence, used for dots and stacked bars. */
export function valenceSwatch(valence: CitationValence | string): string {
  switch (valence) {
    case 'good':
      return 'bg-green-400';
    case 'poor':
      return 'bg-red-400';
    default:
      return 'bg-glass-400';
  }
}

/** Text colour per valence. */
export function valenceText(valence: CitationValence | string): string {
  switch (valence) {
    case 'good':
      return 'text-green-300';
    case 'poor':
      return 'text-red-300';
    default:
      return 'text-glass-400';
  }
}

export function isKnownFacet(facet: string): facet is CitationFacet {
  return ['communication', 'expertise', 'follow_through', 'handoff', 'ownership', 'responsiveness'].includes(facet);
}

/** Formats a UTC hour index as `08:00`. */
export function formatUtcHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/** Converts a UTC hour to the viewer's local hour for the given reference date. */
export function utcHourToLocal(hour: number, reference: Date = new Date()): number {
  const d = new Date(Date.UTC(reference.getUTCFullYear(), reference.getUTCMonth(), reference.getUTCDate(), hour));
  return d.getHours();
}
